/**
 * A shape of the history as the 3D Local Map's graph payload: a node per file
 * with its archetype and its symbol count, a link per directed edge with its
 * count, and nothing the sequence does not carry. The docs of that commit are
 * not in the bundle, so a node has no symbol names, no doc path and no
 * dependency lines; the view sizes it by the count and labels it by the count.
 * Its related documents are the links its authored words made then
 * ({@link relatedDocLinksOf}), drawn as today's Related Docs are.
 *
 * Pure-function module: no DOM.
 *
 * @module snapshot
 */

import { edgeEnds, type Shape } from "@live-documentation/engine/live-docs/history/shape";

import { documentedReferences } from "../../shared/graph";
import type { RelatedDocLink } from "../../shared/staticExplorerData";
import type { ExplorerGraphPayload, ExplorerLinkPayload, ExplorerNodePayload } from "../../shared/types";

const compare = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/** The payload the views render, for the shape at one step. */
export function snapshotGraphOf(shape: Shape): ExplorerGraphPayload {
  const inbound = new Map<string, string[]>();
  const links: ExplorerLinkPayload[] = [];
  for (const [key, count] of shape.edges) {
    const [from, to] = edgeEnds(key);
    links.push({ source: from, target: to, kind: "dependency", referenceCount: count });
    const users = inbound.get(to) ?? [];
    users.push(from);
    inbound.set(to, users);
  }
  links.sort((a, b) => compare(a.source as string, b.source as string) || compare(a.target as string, b.target as string));
  const nodes: ExplorerNodePayload[] = [...shape.files.keys()].sort(compare).map((path) => {
    const file = shape.files.get(path)!;
    return {
      id: path,
      name: path.slice(path.lastIndexOf("/") + 1),
      codePath: path,
      codeRelativePath: path,
      docPath: "",
      docRelativePath: "",
      archetype: file.archetype,
      dependencies: [],
      dependents: (inbound.get(path) ?? []).sort(compare),
      missingDependencies: [],
      publicSymbols: [],
      symbolCount: file.symbols
    };
  });
  // The sequence keeps no dependency lines, so what lay outside the scan then is not known and not said.
  return { nodes, links, stats: { nodes: nodes.length, links: links.length, references: documentedReferences(links) } };
}

/**
 * The links from each file's authored words to a document at the step, as today's bundle names its Related Docs: what
 * the files cited then. The documents that cited a file at the step are not in the sequence yet (2026-10-22).
 */
export function relatedDocLinksOf(shape: Shape): RelatedDocLink[] {
  return [...shape.docLinks].map(edgeEnds).sort((a, b) => compare(a[0], b[0]) || compare(a[1], b[1]))
    .map(([sourceId, targetPath]) => ({ sourceId, targetPath, direction: "cites" }));
}
